<template>
	<div class="fd-widgets image-selector">
		<div class="fd-widget display-standard">
			<div class="pos-label app-label">{{ env.msg('bulkMailLink.url') }}:</div>
			<div class="pos-field app-field url-pick-row"><input type="url" v-model="data.src" placeholder="https://..."> <button @click="browserOpen=true">{{ env.msg('butt.select') }}</button></div>
		</div>
		<div class="fd-widget display-standard">
			<div class="pos-label app-label">{{ env.msg('th.description') }}:</div>
			<div class="pos-field app-field"><input type="text" v-model="data.alt" class="width100p"></div>
		</div>
		<div class="fd-widget display-standard">
			<div class="pos-label app-label">{{ env.msg('image.dimensions') }}:</div>
			<div class="pos-field app-field dimensions-row" style="column-gap: 32px;flex-wrap:wrap">
				<span class="dimensions-row">
					<input type="number" min="0" :placeholder="env.msg('profile.logoWidth').toLowerCase()" v-model="pixelWidth" @input="widthChanged">
					<icon-button :name="preserveAspect ? 'lock':'unlock'" @click="preserveAspect = !preserveAspect"></icon-button>
					<input type="number" min="0" :placeholder="env.msg('profile.logoHeight').toLowerCase()" v-model="pixelHeight" @input="heightChanged">
				</span>
				<span class="dimensions-row" style="column-gap: 8px">
					<a href="#" @click="setImageRatio(0.5)">½</a>
					<icon-button name="image" @click="setImageRatio(1)"></icon-button>
					<a href="#" @click="setImageRatio(2)">2&times;</a>
				</span>
			</div>
		</div>
		<div class="fd-widget display-field">
			<div class="app-field pos-wide">
				<div class="image-preview-input likeInput" :class="{'image-preview-error' : loadingError}">
					<img v-if="previewUrlValid" :src="previewUrl" ref="previewImg" @load="imageLoaded" @error="imageLoadError" :style="imageStyle" :alt="data.alt">
					<sym-icon v-else name="photo" style="font-size:56px;opacity: 0.1"></sym-icon>
				</div>
			</div>
		</div>
	</div>
	<dialog-window v-if="browserOpen" icon="insert-image" :title="'Výběr obrázku ze serveru'" :moveable="true" :resizable="true" :width="600" @close="closeBrowser" show="modal">
		<open-file-browser volume="attFile" :preview="true" filter="image/" open-button-label="Vybrat"
						   open-button-icon="download" @cancel="closeBrowser" @selected="selectUrl"/>
	</dialog-window>
</template>

<script setup lang="ts">
import {ref, computed, type CSSProperties} from "vue";
import IconButton from "@fd/helpers/IconButton.vue";
import {injectEnv} from "@fd/Environment.ts";
import useContainer, {type ContainerProps, containerProps} from "@fd/containers/Container.ts";
import {CustomPart} from "@t/fd/CustomPart.ts";
import {ImageData} from "@fd/editors/html/commands/Image.ts";
import DialogWindow from "@/dialog/DialogWindow.vue";
import OpenFileBrowser from "@/vue/file/OpenFileBrowser.vue";
import type {FolderStructure} from "@/vue/file/FolderStructure.ts";
import SymIcon from "@fd/helpers/SymIcon.vue";


const uprops = defineProps({...containerProps});
const props = uprops as ContainerProps;
const part = computed<CustomPart>(() => props.part as CustomPart);
const {visible, componentOfPart, partRef, localData, ro, vm, clazz, style} = useContainer(props);
const data = computed<ImageData>(() => { return localData.value as ImageData });
const browserOpen = ref<boolean>(false);

const env = injectEnv();

const pixelWidth = computed<number>({
	get():number { return pixelSize(data.value.width); },
	set(value:number):void { data.value.width = value == null || value === 0 ? null : value+"px"; }
});

const pixelHeight = computed<number>({
	get():number { return pixelSize(data.value.height); },
	set(value:number):void { data.value.height = value == null || value === 0 ? null : value+"px"; }
});

const preserveAspect = ref<boolean>(true);

// Dimensions of loaded image
const originalWidth = ref<string|null>(null);
const originalHeight = ref<string|null>(null);

const previewUrl = computed<string>(() => data.value.src);
const previewUrlValid = computed<boolean>(() => validUrl(previewUrl.value));
const loadingError = ref<boolean>(false);
const previewImg = ref<HTMLImageElement | null>(null);
const imageStyle = computed<CSSProperties>(() => {
	const s: CSSProperties = {};
	if (data.value?.width) s.width = data.value.width;
	if (data.value?.height) s.height = data.value.height;
	return s;
});

function closeBrowser() {
	browserOpen.value = false;
}

function selectUrl(items:FolderStructure[]) {
	console.log("selectUrl "+JSON.stringify(items));
	if (items.length > 0) data.value.src = items[0].url;
	closeBrowser();
}

function validUrl(url:string):boolean {
	if (!url) return false;
	try {
		const u = new URL(url); // Throws if invalid
		const isHttp = u.protocol === "https:" || u.protocol === "http:";
		const hasPath = !!u.pathname && u.pathname !== "/";
		return isHttp && hasPath;
	} catch(e) {
		return false;
	}
}

function imageLoaded() {
	if (previewImg.value) {
		originalWidth.value = data.value.width = previewImg.value.naturalWidth+"px";
		originalHeight.value = data.value.height = previewImg.value.naturalHeight+"px";
		if (data.value.alt.length === 0) {
			const u = new URL(previewImg.value.src);
			let path = u.pathname;
			const idx = path.lastIndexOf('/');
			if (idx >= 0) path = path.substring(idx + 1);
			data.value.alt = path;
		}
	}
	loadingError.value = false;
}

function imageLoadError() {
	originalWidth.value = data.value.width = null;
	originalHeight.value = data.value.height = null;
	loadingError.value = true;
}

function pixelSize(size:string|number|null|undefined):number {
	if (size == null) return 0;
	if (typeof size === 'number') return size;
	const pxIndex = size.indexOf("px");
	if (pxIndex > 0) {
		return parseFloat(size.substring(0, pxIndex));
	}
	return 0;
}

function widthChanged() {
	let pixelWidth = pixelSize(originalWidth.value);
	let pixelHeight = pixelSize(originalHeight.value);
	if (preserveAspect.value && pixelWidth > 0 && pixelHeight > 0) {
		const aspect = pixelWidth / pixelHeight;
		data.value.height = Math.round(pixelSize(data.value.width) / aspect)+"px";
	}
}

function heightChanged() {
	let pixelWidth = pixelSize(originalWidth.value);
	let pixelHeight = pixelSize(originalHeight.value);
	if (preserveAspect.value && pixelWidth > 0 && pixelHeight > 0) {
		const aspect = pixelWidth / pixelHeight;
		data.value.width = Math.round(pixelSize(data.value.height) * aspect)+"px";
	}
}

function setImageRatio(ratio:number):void {
	data.value.width = Math.round(pixelSize(originalWidth.value) * ratio)+"px";
	data.value.height = Math.round(pixelSize(originalHeight.value) * ratio)+"px";
}
</script>

<style>
.url-pick-row {
	display: grid;
	grid-template-columns: 1fr min-content;
	align-items: baseline;
	column-gap: var(--input-spacing);
}
.dimensions-row {
	display: flex;
	flex-direction: row;
	column-gap: var(--input-spacing);
	row-gap: 4px;
	align-items: baseline;
	justify-content: flex-start;
}


.form-col-gap {
	column-gap: calc(2 * var(--form-h-padding));
}
.form-row-gap {
	row-gap: calc(2 * var(--form-v-padding));
}

.image-selector {
}

.image-selector-urls {
	display: grid;
	grid-template-columns: min-content 1fr min-content;
	align-items: baseline;
}
.image-dimensions,
.image-alt {
	display: flex;
	flex-direction: row;
	align-items: baseline;
}

.image-preview-input.likeInput {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-height: 200px;
	max-height: 500px;
	width: 100%;
	padding:var(--input-radius);
	overflow: auto;

	&.image-preview-error {
		background-color: var(--input-error-bg);
	}
}
</style>